import React from "react";
import { Box, Typography } from "@mui/material";
import { MACgraphModel } from "pages/Dashboards/ProjectRegister/data/MACgraphModel";
import CustomDataGrid from "./CustomDataGrid";
import { generateTableColumn } from "pages/Dashboards/ProjectRegister/data/metricsUtils";
import { Constants, Currency } from "constants/constant";
import { normalizeMeasureLabel } from "pages/Dashboards/ProjectRegister/data/measureLabelUtils";
import { formatEmissionValue } from "utilities/utilities";

interface PortfolioSummaryTableProps {
  macData: MACgraphModel[];
  currency?: string;
  groupBy?: string;
  chartMeasureOrder?: string[];
}

const GROUP_BY_COLUMN_LABEL: Record<string, string> = {
  Organization: "Organization",
  Location: "Location",
  Type: "Measure Type",
  Category: "Measure Category",
  Individual: "Measure",
};

const sortRowsByChartMeasureOrder = (
  rows: MACgraphModel[],
  chartMeasureOrder: string[] | undefined
) => {
  if (!Array.isArray(chartMeasureOrder) || chartMeasureOrder.length === 0) {
    return [...rows].sort((a, b) => (a.mac ?? 0) - (b.mac ?? 0));
  }

  const orderMap = new Map<string, number>();
  chartMeasureOrder.forEach((label, index) => {
    const normalizedLabel = normalizeMeasureLabel(label);
    if (!normalizedLabel || orderMap.has(normalizedLabel)) {
      return;
    }
    orderMap.set(normalizedLabel, index);
  });

  if (orderMap.size === 0) {
    return [...rows].sort((a, b) => (a.mac ?? 0) - (b.mac ?? 0));
  }

  return rows
    .map((row, index) => ({ row, index }))
    .sort((left, right) => {
      const leftOrder = orderMap.get(normalizeMeasureLabel(left.row.locationName));
      const rightOrder = orderMap.get(normalizeMeasureLabel(right.row.locationName));

      const safeLeftOrder = leftOrder ?? Number.MAX_SAFE_INTEGER;
      const safeRightOrder = rightOrder ?? Number.MAX_SAFE_INTEGER;

      if (safeLeftOrder !== safeRightOrder) {
        return safeLeftOrder - safeRightOrder;
      }

      return left.index - right.index;
    })
    .map((entry) => entry.row);
};

const PortfolioSummaryTable: React.FC<PortfolioSummaryTableProps> = ({
  macData,
  currency = "$",
  groupBy,
  chartMeasureOrder,
}) => {
  if (!macData || macData.length === 0) return null;

  const UNIT_TCO2E = Constants.UNIT_TCO2E;
  const currencyCode = Currency.currency.find((c) => c.value === currency)?.value ?? currency;
  const firstColLabel = (groupBy && GROUP_BY_COLUMN_LABEL[groupBy]) ?? "Location / Measure";

  const rows = sortRowsByChartMeasureOrder(macData, chartMeasureOrder);

  const columns = [
    {
      field: "locationName",
      headerClassName: "table-header",
      headerName: firstColLabel,
      minWidth: 200,
      flex: 2,
      renderCell: (params: any) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {params.value}
        </Box>
      ),
    },
    generateTableColumn("annualEmissionReduction", `Emissions Reduction (${UNIT_TCO2E})`, 190),
    {
      ...generateTableColumn("averageAnnualROI", "Average Annual ROI (%)", 180),
      renderCell: (params: any) => (
        <div style={{ textAlign: "center", width: "80%" }}>
          {params.value == null ? "-" : formatEmissionValue(params.value)}
        </div>
      ),
    },
    generateTableColumn("annualNPV", `Annual NPV (${currencyCode})`, 160),
    generateTableColumn("capExInvestment", `CapEx Investment (${currencyCode})`, 160),
    generateTableColumn("mac", `MAC (${currencyCode}/${UNIT_TCO2E})`, 160),
  ];

  return (
    <>
      <Typography
        sx={{
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "#0f2b52",
          px: 2,
          py: 1.25,
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        Portfolio Summary Table
      </Typography>
      <CustomDataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.locationName}
      />
    </>
  );
};

export default PortfolioSummaryTable;
